import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { Router } from '@angular/router';

import { take } from 'rxjs';

import { ProductBarcodeScannerModalLoaderService } from '@hdms/product-barcode-scanner/feature';
import { GlobalSearchRoutes } from '@hdms/shared/common-types';
import { SearchSuggestionService, URLS, getSearchUrl } from '@hdms/shared/page-frame/data-access';
import { IconsName, SearchFieldComponent, SizeOptionType } from '@hdms/shared/ui-core';
import { AnalyticsService, FeatureToggleService, PlatformService, WindowRefService } from '@hdms/shared/util-core';

import { FT_BARCODE_SCANNING_MODAL } from '../../../config/toggles';

@Component({
  selector: 'shrd-pf-ft-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderSearchInputComponent implements AfterViewInit {
  searchTerm$ = this.suggestionService.getSearchTerm$();
  readonly maxLength = 64;

  @Input() autofocus: boolean;
  @Input() iconSize: SizeOptionType = 'small';
  @Input() usePreloader = false;
  @Input() loading = false;

  @Output() focusChange = new EventEmitter<boolean>();

  @ViewChild(SearchFieldComponent) private readonly searchField: SearchFieldComponent;
  @ViewChild('modal', { read: ViewContainerRef }) modal: ViewContainerRef;

  readonly iconsName = IconsName;
  readonly isBarcodeScannerEnabled = this.suggestionService.isBarcodeSupported();

  constructor(
    private readonly productBarcodeScannerModalLoaderService: ProductBarcodeScannerModalLoaderService,
    private readonly suggestionService: SearchSuggestionService,
    private readonly winRef: WindowRefService,
    private readonly router: Router,
    private readonly ftService: FeatureToggleService,
    private readonly platform: PlatformService,
    private readonly analyticsService: AnalyticsService
  ) {}

  ngAfterViewInit(): void {
    // adding search term will affect cached SSR html, so exclude it on server side
    this.platform.isBrowser && this.pickSearchTermFromQueryParams();
  }

  onSearchAction(term: string): void {
    // Redirect to search page
    if (term) {
      this.analyticsService.sendEvent({
        category: 'ProductInfo',
        action: 'FindProducts',
        label: `SiteSearchStandard|${term}|Search Icon`
      });
      this.location.assign(getSearchUrl(term));
    }
  }

  updateSearchTerm(term: string): void {
    this.suggestionService.updateSearchTerm(term);
  }

  focus(): void {
    this.searchField.focus();
  }

  blur(): void {
    this.searchField.blur();
  }

  onBarcodeScan(): void {
    this.analyticsService.sendEvent({
      category: 'ProductInfo',
      action: 'FindProducts',
      label: 'OpenCamera|Search'
    });

    if (this.ftService.getToggleById(FT_BARCODE_SCANNING_MODAL)) {
      this.productBarcodeScannerModalLoaderService.loadAndOpen(this.modal);
      return;
    }

    this.winRef.nativeWindow.location.assign(URLS.barcodeScan);
  }

  private pickSearchTermFromQueryParams(): void {
    const searchTerm =
      this.location.pathname.startsWith(`/${GlobalSearchRoutes.BASE}`) &&
      new URL(this.location.href).searchParams.get('text');

    if (searchTerm) {
      // just set the value but don't emit to avoid triggering a request,
      // but trigger it on first search field focus.
      this.searchField.searchControl.setValue(searchTerm, { emitEvent: false });
      this.focusChange.pipe(take(1)).subscribe(() => {
        this.updateSearchTerm(searchTerm);
      });
    }
  }

  private get location(): Location {
    return this.winRef.nativeWindow.location;
  }
}
